import { useState, useEffect } from 'react';
import axios from 'axios';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const API = 'https://golf-reservation-backend.onrender.com';

export default function AdminPage({ onLogout }) {
  const [tab, setTab] = useState('users');
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState(0); // 0 = 전체
  const [reportExecutive, setReportExecutive] = useState('all');
  const [users, setUsers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [membershipUsers, setMembershipUsers] = useState([]);
  const [membershipRules, setMembershipRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingMembership, setEditingMembership] = useState(null);

  const [userForm, setUserForm] = useState({
    name: '', email: '', role: 'executive',
    telegram_chat_id: '', assigned_secretary_id: '',
  });

  const [membershipForm, setMembershipForm] = useState({
    club_name: '', member_type: '',
  });

  const [membershipUserForm, setMembershipUserForm] = useState({
    membership_id: '', user_id: '',
  });

  const [ruleForm, setRuleForm] = useState({
    membership_id: '',
    weekday_limit: 6,
    weekend_limit: 3,
    delegation_weekday_limit: 0,
    delegation_weekend_limit: 0,
    is_named: true,
    note: '',
  });

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const fetchAll = async () => {
    try {
      const [u, m, r, mu, mr] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/memberships`),
        axios.get(`${API}/admin/reservations`),
        axios.get(`${API}/membership-users`),
        axios.get(`${API}/membership-rules`),
      ]);
      setUsers(u.data);
      setMemberships(m.data);
      setReservations(r.data);
      setMembershipUsers(mu.data);
      setMembershipRules(mr.data);
    } catch (e) { console.log(e); }
  };

  const secretaries = users.filter(u => u.role === 'secretary');
  const executives = users.filter(u => u.role === 'executive');

  // ── 사용자 핸들러 ──
  const handleAddUser = async () => {
    if (!userForm.name || !userForm.email) { alert('이름과 이메일은 필수예요!'); return; }
    setLoading(true);
    try {
      const payload = { ...userForm };
      if (!payload.assigned_secretary_id) delete payload.assigned_secretary_id;
      if (!payload.telegram_chat_id) delete payload.telegram_chat_id;
      await axios.post(`${API}/users`, payload);
      alert('사용자가 등록됐어요!');
      setUserForm({ name: '', email: '', role: 'executive', telegram_chat_id: '', assigned_secretary_id: '' });
      fetchAll();
    } catch (e) { alert('등록 실패: ' + (e.response?.data?.error || e.message)); }
    setLoading(false);
  };

  const handleUpdateUser = async () => {
    if (!editingUser.name || !editingUser.email) { alert('이름과 이메일은 필수예요!'); return; }
    setLoading(true);
    try {
      const payload = { ...editingUser };
      if (!payload.assigned_secretary_id) delete payload.assigned_secretary_id;
      if (!payload.telegram_chat_id) delete payload.telegram_chat_id;
      await axios.patch(`${API}/users/${editingUser.id}`, payload);
      alert('수정됐어요!');
      setEditingUser(null);
      fetchAll();
    } catch (e) { alert('수정 실패: ' + (e.response?.data?.error || e.message)); }
    setLoading(false);
  };

  const handleDeleteUser = async (id, name) => {
    if (!window.confirm(`${name}님을 삭제할까요?`)) return;
    try {
      await axios.delete(`${API}/users/${id}`);
      fetchAll();
    } catch (e) { alert('삭제 실패: ' + (e.response?.data?.error || e.message)); }
  };

  // ── 회원권 핸들러 ──
  const handleAddMembership = async () => {
    if (!membershipForm.club_name || !membershipForm.member_type) {
      alert('골프장 이름과 회원권 종류를 입력해주세요!'); return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/memberships`, membershipForm);
      setMembershipForm({ club_name: '', member_type: '' });
      fetchAll();
    } catch (e) { alert('등록 실패: ' + (e.response?.data?.error || e.message)); }
    setLoading(false);
  };

  const handleUpdateMembership = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API}/memberships/${editingMembership.id}`, {
        club_name: editingMembership.club_name,
        member_type: editingMembership.member_type,
      });
      setEditingMembership(null);
      fetchAll();
    } catch (e) { alert('수정 실패: ' + (e.response?.data?.error || e.message)); }
    setLoading(false);
  };

  const handleDeleteMembership = async (id, name) => {
    if (!window.confirm(`${name} 회원권을 삭제할까요?`)) return;
    try {
      await axios.delete(`${API}/memberships/${id}`);
      fetchAll();
    } catch (e) { alert('삭제 실패: ' + (e.response?.data?.error || e.message)); }
  };

  // ── 임원 지정 핸들러 ──
  const handleAddMembershipUser = async () => {
    if (!membershipUserForm.membership_id || !membershipUserForm.user_id) {
      alert('회원권과 임원을 선택해주세요!'); return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/membership-users`, membershipUserForm);
      setMembershipUserForm({ membership_id: '', user_id: '' });
      fetchAll();
    } catch (e) { alert('지정 실패: ' + (e.response?.data?.error || e.message)); }
    setLoading(false);
  };

  const handleDeleteMembershipUser = async (id, name) => {
    if (!window.confirm(`${name} 지정을 해제할까요?`)) return;
    try {
      await axios.delete(`${API}/membership-users/${id}`);
      fetchAll();
    } catch (e) { alert('삭제 실패: ' + (e.response?.data?.error || e.message)); }
  };

  // ── 예약 규칙 핸들러 ──
  const handleDeleteRule = async (id, name) => {
  if (!window.confirm(`${name} 규칙을 삭제할까요?`)) return;
  try {
    await axios.delete(`${API}/membership-rules/${id}`);
    alert('삭제됐어요!');
    fetchAll();
  } catch (e) { alert('삭제 실패: ' + (e.response?.data?.error || e.message)); }
};
  const handleSaveRule = async () => {
    if (!ruleForm.membership_id) { alert('회원권을 선택해주세요!'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/membership-rules`, ruleForm);
      alert('규칙이 저장됐어요!');
      setRuleForm({ membership_id: '', weekday_limit: 6, weekend_limit: 3, delegation_weekday_limit: 0, delegation_weekend_limit: 0, is_named: true, note: '' });
      fetchAll();
    } catch (e) { alert('저장 실패: ' + (e.response?.data?.error || e.message)); }
    setLoading(false);
  };

  // ── 레포트 계산 ──
  const buildReport = () => {
    const confirmedRes = reservations.filter(r => r.status === 'confirmed' || r.status === 'pending');

    const filtered = confirmedRes.filter(r => {
      const d = new Date(r.confirmed_date || r.desired_date);
      if (isNaN(d)) return false;
      if (d.getFullYear() !== reportYear) return false;
      if (reportMonth !== 0 && d.getMonth() + 1 !== reportMonth) return false;
      if (reportExecutive !== 'all' && r.requester_id !== reportExecutive) return false;
      return true;
    });

    // 임원별 집계
    const byExecutive = {};
    executives.forEach(e => {
      byExecutive[e.id] = { name: e.name, weekday: 0, weekend: 0, total: 0, monthly: {} };
    });

    filtered.forEach(r => {
      const exec = byExecutive[r.requester_id];
      if (!exec) return;
      const d = new Date(r.confirmed_date || r.desired_date);
      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
      const month = d.getMonth() + 1;

      if (isWeekend) exec.weekend++; else exec.weekday++;
      exec.total++;

      if (!exec.monthly[month]) exec.monthly[month] = { weekday: 0, weekend: 0 };
      if (isWeekend) exec.monthly[month].weekend++; else exec.monthly[month].weekday++;
    });

    // 월별 전체 집계
    const byMonth = {};
    filtered.forEach(r => {
      const d = new Date(r.confirmed_date || r.desired_date);
      const month = d.getMonth() + 1;
      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
      if (!byMonth[month]) byMonth[month] = { weekday: 0, weekend: 0, total: 0 };
      if (isWeekend) byMonth[month].weekend++; else byMonth[month].weekday++;
      byMonth[month].total++;
    });

    const totalWeekday = filtered.filter(r => { const d = new Date(r.confirmed_date || r.desired_date); return d.getDay() !== 0 && d.getDay() !== 6; }).length;
    const totalWeekend = filtered.filter(r => { const d = new Date(r.confirmed_date || r.desired_date); return d.getDay() === 0 || d.getDay() === 6; }).length;

    return { byExecutive, byMonth, totalWeekday, totalWeekend, total: filtered.length };
  };

  const statusLabel = (status) => {
    if (status === 'pending') return { text: '검토중', color: '#f59e0b', bg: '#fef3c7' };
    if (status === 'confirmed') return { text: '확정', color: '#059669', bg: '#d1fae5' };
    if (status === 'rejected') return { text: '거절', color: '#dc2626', bg: '#fee2e2' };
  };

  const roleLabel = (role) => {
    if (role === 'executive') return { text: '임원', color: '#7c3aed', bg: '#ede9fe' };
    if (role === 'secretary') return { text: '비서', color: '#0369a1', bg: '#e0f2fe' };
    if (role === 'admin') return { text: '관리자', color: '#b45309', bg: '#fef3c7' };
  };

  const getReservationsForDate = (date) => {
    const dateStr = date.toLocaleDateString('sv-SE');
    return reservations.filter(r => (r.confirmed_date || r.desired_date) === dateStr);
  };

  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    const list = getReservationsForDate(date);
    if (list.length === 0) return null;
    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '2px', marginTop: '2px', flexWrap: 'wrap' }}>
        {list.slice(0, 3).map((r, i) => {
          const s = statusLabel(r.status);
          return <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: s.color }} />;
        })}
      </div>
    );
  };

  const selectedDateReservations = selectedDate ? getReservationsForDate(selectedDate) : [];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <p style={styles.headerSub}>시스템 관리</p>
          <p style={styles.headerName}>어드민 페이지 ⛳</p>
        </div>
        <button style={styles.logoutBtn} onClick={onLogout}>로그아웃</button>
      </div>

      <div style={styles.content}>
        <div style={styles.tabRow}>
          {[
            { key: 'users', label: '사용자' },
            { key: 'memberships', label: '회원권' },
            { key: 'assignment', label: '임원지정' },
            { key: 'rules', label: '예약규칙' },
            { key: 'reservations', label: '예약현황' },
            { key: 'report', label: '📊 레포트' },
          ].map(t => (
            <button
              key={t.key}
              style={{ ...styles.tab, ...(tab === t.key ? styles.tabActive : {}) }}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── 사용자 관리 ── */}
        {tab === 'users' && (
          <div>
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>새 사용자 등록</h2>
              <div style={styles.formGrid}>
                <div>
                  <label style={styles.label}>이름 *</label>
                  <input style={styles.input} placeholder="홍길동"
                    value={userForm.name}
                    onChange={e => setUserForm({ ...userForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <label style={styles.label}>이메일 *</label>
                  <input style={styles.input} placeholder="hong@company.com"
                    value={userForm.email}
                    onChange={e => setUserForm({ ...userForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <label style={styles.label}>역할 *</label>
                  <select style={styles.input} value={userForm.role}
                    onChange={e => setUserForm({ ...userForm, role: e.target.value })}
                  >
                    <option value="executive">임원</option>
                    <option value="secretary">비서</option>
                    <option value="admin">관리자</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>텔레그램 Chat ID</label>
                  <input style={styles.input} placeholder="123456789"
                    value={userForm.telegram_chat_id}
                    onChange={e => setUserForm({ ...userForm, telegram_chat_id: e.target.value })}
                  />
                </div>
                {userForm.role === 'executive' && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={styles.label}>담당 비서</label>
                    <select style={styles.input} value={userForm.assigned_secretary_id}
                      onChange={e => setUserForm({ ...userForm, assigned_secretary_id: e.target.value })}
                    >
                      <option value="">선택 안 함</option>
                      {secretaries.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <button style={styles.addBtn} onClick={handleAddUser} disabled={loading}>
                {loading ? '등록 중...' : '+ 사용자 등록'}
              </button>
            </div>

            <h2 style={styles.sectionTitle}>사용자 목록 ({users.length}명)</h2>
            {users.map(u => {
              const r = roleLabel(u.role);
              const isEditing = editingUser?.id === u.id;
              return (
                <div key={u.id} style={styles.card}>
                  {!isEditing ? (
                    <div>
                      <div style={styles.userHeader}>
                        <div style={styles.userInfo}>
                          <div style={styles.avatar}>{u.name.charAt(0)}</div>
                          <div>
                            <p style={styles.userName}>{u.name}</p>
                            <p style={styles.userEmail}>{u.email}</p>
                          </div>
                        </div>
                        <span style={{ ...styles.roleBadge, color: r.color, backgroundColor: r.bg }}>{r.text}</span>
                      </div>
                      <p style={styles.userDetail}>텔레그램 ID: {u.telegram_chat_id || '미등록'}</p>
                      {u.role === 'executive' && (
                        <p style={styles.userDetail}>
                          담당 비서: {users.find(s => s.id === u.assigned_secretary_id)?.name || '미배정'}
                        </p>
                      )}
                      <div style={styles.actionRow}>
                        <button style={styles.editBtn} onClick={() => setEditingUser({ ...u })}>수정</button>
                        <button style={styles.deleteBtn} onClick={() => handleDeleteUser(u.id, u.name)}>삭제</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h3 style={styles.cardTitle}>사용자 수정</h3>
                      <div style={styles.formGrid}>
                        <div>
                          <label style={styles.label}>이름</label>
                          <input style={styles.input} value={editingUser.name}
                            onChange={e => setEditingUser({ ...editingUser, name: e.target.value })}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>이메일</label>
                          <input style={styles.input} value={editingUser.email}
                            onChange={e => setEditingUser({ ...editingUser, email: e.target.value })}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>역할</label>
                          <select style={styles.input} value={editingUser.role}
                            onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}
                          >
                            <option value="executive">임원</option>
                            <option value="secretary">비서</option>
                            <option value="admin">관리자</option>
                          </select>
                        </div>
                        <div>
                          <label style={styles.label}>텔레그램 Chat ID</label>
                          <input style={styles.input} value={editingUser.telegram_chat_id || ''}
                            onChange={e => setEditingUser({ ...editingUser, telegram_chat_id: e.target.value })}
                          />
                        </div>
                        {editingUser.role === 'executive' && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={styles.label}>담당 비서</label>
                            <select style={styles.input} value={editingUser.assigned_secretary_id || ''}
                              onChange={e => setEditingUser({ ...editingUser, assigned_secretary_id: e.target.value })}
                            >
                              <option value="">선택 안 함</option>
                              {secretaries.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      <div style={styles.actionRow}>
                        <button style={styles.editBtn} onClick={() => setEditingUser(null)}>취소</button>
                        <button style={styles.addBtn} onClick={handleUpdateUser} disabled={loading}>
                          {loading ? '저장 중...' : '저장'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── 회원권 관리 ── */}
        {tab === 'memberships' && (
          <div>
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>새 회원권 등록</h2>
              <div style={styles.formGrid}>
                <div>
                  <label style={styles.label}>골프장 이름 *</label>
                  <input style={styles.input} placeholder="예: 이포CC"
                    value={membershipForm.club_name}
                    onChange={e => setMembershipForm({ ...membershipForm, club_name: e.target.value })}
                  />
                </div>
                <div>
                  <label style={styles.label}>회원권 종류 *</label>
                  <input style={styles.input} placeholder="예: 정회원, 무기명"
                    value={membershipForm.member_type}
                    onChange={e => setMembershipForm({ ...membershipForm, member_type: e.target.value })}
                  />
                </div>
              </div>
              <button style={styles.addBtn} onClick={handleAddMembership} disabled={loading}>
                {loading ? '등록 중...' : '+ 회원권 등록'}
              </button>
            </div>

            <h2 style={styles.sectionTitle}>회원권 목록 ({memberships.length}개)</h2>
            {memberships.map(m => {
              const isEditing = editingMembership?.id === m.id;
              return (
                <div key={m.id} style={styles.card}>
                  {!isEditing ? (
                    <div style={styles.membershipRow}>
                      <div>
                        <p style={styles.userName}>{m.club_name}</p>
                        <p style={styles.userEmail}>{m.member_type}</p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button style={styles.editBtn} onClick={() => setEditingMembership({ ...m })}>수정</button>
                        <button style={styles.deleteBtn}
                          onClick={() => handleDeleteMembership(m.id, `${m.club_name} ${m.member_type}`)}>삭제</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h3 style={styles.cardTitle}>회원권 수정</h3>
                      <div style={styles.formGrid}>
                        <div>
                          <label style={styles.label}>골프장 이름</label>
                          <input style={styles.input} value={editingMembership.club_name}
                            onChange={e => setEditingMembership({ ...editingMembership, club_name: e.target.value })}
                          />
                        </div>
                        <div>
                          <label style={styles.label}>회원권 종류</label>
                          <input style={styles.input} value={editingMembership.member_type}
                            onChange={e => setEditingMembership({ ...editingMembership, member_type: e.target.value })}
                          />
                        </div>
                      </div>
                      <div style={styles.actionRow}>
                        <button style={styles.editBtn} onClick={() => setEditingMembership(null)}>취소</button>
                        <button style={styles.addBtn} onClick={handleUpdateMembership} disabled={loading}>
                          {loading ? '저장 중...' : '저장'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── 임원 지정 ── */}
        {tab === 'assignment' && (
          <div>
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>회원권별 임원 지정</h2>
              <p style={styles.descText}>기명 회원권에 특정 임원을 지정하면 해당 임원만 그 회원권으로 예약할 수 있어요. 무기명 회원권은 지정하지 않으면 돼요.</p>
              <div style={styles.formGrid}>
                <div>
                  <label style={styles.label}>회원권 *</label>
                  <select style={styles.input} value={membershipUserForm.membership_id}
                    onChange={e => setMembershipUserForm({ ...membershipUserForm, membership_id: e.target.value })}
                  >
                    <option value="">선택해주세요</option>
                    {memberships.map(m => (
                      <option key={m.id} value={m.id}>{m.club_name} · {m.member_type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>임원 *</label>
                  <select style={styles.input} value={membershipUserForm.user_id}
                    onChange={e => setMembershipUserForm({ ...membershipUserForm, user_id: e.target.value })}
                  >
                    <option value="">선택해주세요</option>
                    {executives.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button style={styles.addBtn} onClick={handleAddMembershipUser} disabled={loading}>
                {loading ? '지정 중...' : '+ 임원 지정'}
              </button>
            </div>

            <h2 style={styles.sectionTitle}>지정 현황</h2>
            {memberships.map(m => {
              const assigned = membershipUsers.filter(mu => mu.membership_id === m.id);
              if (assigned.length === 0) return null;
              return (
                <div key={m.id} style={styles.card}>
                  <p style={styles.userName}>{m.club_name} · {m.member_type}</p>
                  <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {assigned.map(mu => (
                      <div key={mu.id} style={styles.assignedBadge}>
                        <span>{mu.user?.name}</span>
                        <button style={styles.removeBadgeBtn}
                          onClick={() => handleDeleteMembershipUser(mu.id, mu.user?.name)}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {membershipUsers.length === 0 && (
              <div style={styles.emptyBox}>아직 지정된 임원이 없어요</div>
            )}
          </div>
        )}

        {/* ── 예약 규칙 ── */}
        {tab === 'rules' && (
          <div>
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>회원권별 예약 규칙 설정</h2>
              <p style={styles.descText}>회원권을 선택하면 기존 규칙이 자동으로 불러와져요.</p>
              <div style={styles.formGrid}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={styles.label}>회원권 *</label>
                  <select style={styles.input} value={ruleForm.membership_id}
                    onChange={e => {
                      const existing = membershipRules.find(r => r.membership_id === e.target.value);
                      if (existing) {
                        setRuleForm({ ...existing });
                      } else {
                        setRuleForm({ membership_id: e.target.value, weekday_limit: 6, weekend_limit: 3, delegation_weekday_limit: 0, delegation_weekend_limit: 0, is_named: true, note: '' });
                      }
                    }}
                  >
                    <option value="">선택해주세요</option>
                    {memberships.map(m => (
                      <option key={m.id} value={m.id}>{m.club_name} · {m.member_type}</option>
                    ))}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={styles.label}>회원권 유형</label>
                  <select style={styles.input} value={ruleForm.is_named ? 'true' : 'false'}
                    onChange={e => setRuleForm({ ...ruleForm, is_named: e.target.value === 'true' })}
                  >
                    <option value="true">기명회원 (특정 임원 전용)</option>
                    <option value="false">무기명 (누구든 사용 가능)</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>주중 한도 (월)</label>
                  <input style={styles.input} type="number" min="0" max="30" autoComplete="off"
                    value={ruleForm.weekday_limit}
                    onChange={e => setRuleForm({ ...ruleForm, weekday_limit: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <label style={styles.label}>주말 한도 (월)</label>
                  <input style={styles.input} type="number" min="0" max="30" autoComplete="off"
                    value={ruleForm.weekend_limit}
                    onChange={e => setRuleForm({ ...ruleForm, weekend_limit: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <label style={styles.label}>위임 주중 한도 (월)</label>
                  <input style={styles.input} type="number" min="0" max="30" autoComplete="off"
                    value={ruleForm.delegation_weekday_limit}
                    onChange={e => setRuleForm({ ...ruleForm, delegation_weekday_limit: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <label style={styles.label}>위임 주말 한도 (월)</label>
                  <input style={styles.input} type="number" min="0" max="30" autoComplete="off"
                    value={ruleForm.delegation_weekend_limit}
                    onChange={e => setRuleForm({ ...ruleForm, delegation_weekend_limit: parseInt(e.target.value) })}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={styles.label}>메모</label>
                  <input style={styles.input} placeholder="예: 주말 격월 신청"
                    value={ruleForm.note}
                    onChange={e => setRuleForm({ ...ruleForm, note: e.target.value })}
                  />
                </div>
              </div>
              <button style={styles.addBtn} onClick={handleSaveRule} disabled={loading}>
                {loading ? '저장 중...' : '규칙 저장'}
              </button>
            </div>

            <h2 style={styles.sectionTitle}>등록된 규칙 ({membershipRules.length}개)</h2>
            {membershipRules.length === 0 ? (
              <div style={styles.emptyBox}>아직 등록된 규칙이 없어요</div>
            ) : (
              membershipRules.map(r => (
                <div key={r.id} style={styles.card}>
                  <div style={styles.userHeader}>
                    <div>
                      <p style={styles.userName}>{r.membership?.club_name} · {r.membership?.member_type}</p>
                      <p style={styles.userEmail}>{r.is_named ? '기명회원' : '무기명'}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
  <button style={styles.editBtn} onClick={() => setRuleForm({ ...r })}>수정</button>
  <button style={styles.deleteBtn}
    onClick={() => handleDeleteRule(r.id, `${r.membership?.club_name} ${r.membership?.member_type}`)}>
    삭제
  </button>
</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                    <span style={styles.rulePill}>주중 {r.weekday_limit}회</span>
                    <span style={styles.rulePill}>주말 {r.weekend_limit}회</span>
                    {r.delegation_weekday_limit > 0 && <span style={styles.rulePillAmber}>위임주중 {r.delegation_weekday_limit}회</span>}
                    {r.delegation_weekend_limit > 0 && <span style={styles.rulePillAmber}>위임주말 {r.delegation_weekend_limit}회</span>}
                  </div>
                  {r.note && <p style={{ ...styles.userDetail, marginTop: '6px' }}>메모: {r.note}</p>}
                </div>
              ))
            )}
          </div>
        )}

        {/* ── 예약 현황 ── */}
        {tab === 'reservations' && (
          <div>
            <div style={styles.card}>
              <style>{`
                .react-calendar { width: 100%; border: none; font-family: inherit; }
                .react-calendar__tile { padding: 10px 6px; font-size: 13px; }
                .react-calendar__tile--active { background: #92400e !important; color: white !important; border-radius: 6px; }
                .react-calendar__tile--now { background: #fef3c7; border-radius: 6px; }
                .react-calendar__navigation button { font-size: 14px; font-weight: 600; }
              `}</style>
              <Calendar onChange={setSelectedDate} value={selectedDate} tileContent={tileContent} locale="ko-KR" />
            </div>

            {selectedDate ? (
              <div>
                <h2 style={styles.sectionTitle}>
                  {selectedDate.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' })} ({selectedDateReservations.length}건)
                </h2>
                {selectedDateReservations.length === 0 ? (
                  <div style={styles.emptyBox}>이 날짜에 예약이 없어요</div>
                ) : (
                  selectedDateReservations.map(r => {
                    const s = statusLabel(r.status);
                    return (
                      <div key={r.id} style={styles.card}>
                        <div style={styles.userHeader}>
                          <div>
                            <p style={styles.userName}>{r.requester?.name}</p>
                            <p style={styles.userEmail}>{r.membership?.club_name} · {r.membership?.member_type}</p>
                            {r.is_proxy && <p style={{ fontSize: '12px', color: '#7c3aed' }}>대리예약 · {r.actual_user_name}</p>}
                          </div>
                          <span style={{ ...styles.roleBadge, color: s.color, backgroundColor: s.bg }}>{s.text}</span>
                        </div>
                        <p style={styles.userDetail}>희망일: {r.desired_date} {r.desired_time || ''}</p>
                        {r.confirmed_date && <p style={styles.userDetail}>확정일: {r.confirmed_date} {r.confirmed_time || ''}</p>}
                        {r.companions && <p style={styles.userDetail}>동반자: {r.companions}</p>}
                        {r.note && <p style={styles.userDetail}>메모: {r.note}</p>}
                        {r.secretary_direct && (
                          <span style={{ fontSize: '11px', color: '#0369a1', backgroundColor: '#e0f2fe', padding: '2px 8px', borderRadius: '10px', display: 'inline-block', marginTop: '4px' }}>
                            비서직접예약
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <div style={styles.emptyBox}>달력에서 날짜를 선택하면 예약 내역이 표시돼요</div>
            )}
          </div>
        )}
        {/* ── 레포트 탭 ── */}
        {tab === 'report' && (() => {
          const report = buildReport();
          const execList = reportExecutive === 'all'
            ? executives
            : executives.filter(e => e.id === reportExecutive);

          return (
            <div>
              {/* 필터 */}
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>📊 예약 현황 레포트</h2>
                <div style={styles.reportFilterRow}>
                  <div style={styles.reportFilterItem}>
                    <label style={styles.label}>연도</label>
                    <select style={styles.input} value={reportYear} onChange={e => setReportYear(Number(e.target.value))}>
                      {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}년</option>)}
                    </select>
                  </div>
                  <div style={styles.reportFilterItem}>
                    <label style={styles.label}>월</label>
                    <select style={styles.input} value={reportMonth} onChange={e => setReportMonth(Number(e.target.value))}>
                      <option value={0}>전체</option>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{m}월</option>)}
                    </select>
                  </div>
                  <div style={styles.reportFilterItem}>
                    <label style={styles.label}>임원</label>
                    <select style={styles.input} value={reportExecutive} onChange={e => setReportExecutive(e.target.value)}>
                      <option value="all">전체</option>
                      {executives.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* 전체 요약 */}
              <div style={styles.reportSummaryRow}>
                <div style={styles.reportSummaryCard}>
                  <p style={styles.reportSummaryLabel}>총 예약</p>
                  <p style={styles.reportSummaryValue}>{report.total}회</p>
                </div>
                <div style={styles.reportSummaryCard}>
                  <p style={styles.reportSummaryLabel}>주중</p>
                  <p style={{ ...styles.reportSummaryValue, color: '#1d4ed8' }}>{report.totalWeekday}회</p>
                </div>
                <div style={styles.reportSummaryCard}>
                  <p style={styles.reportSummaryLabel}>주말</p>
                  <p style={{ ...styles.reportSummaryValue, color: '#059669' }}>{report.totalWeekend}회</p>
                </div>
              </div>

              {/* 월별 전체 집계 (월 전체 선택 시) */}
              {reportMonth === 0 && (
                <div style={styles.card}>
                  <h3 style={styles.reportTableTitle}>월별 집계 ({reportYear}년)</h3>
                  <table style={styles.table}>
                    <thead>
                      <tr style={styles.tableHead}>
                        <th style={styles.th}>월</th>
                        <th style={styles.th}>주중</th>
                        <th style={styles.th}>주말</th>
                        <th style={styles.th}>합계</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => {
                        const d = report.byMonth[m] || { weekday: 0, weekend: 0, total: 0 };
                        return (
                          <tr key={m} style={styles.tableRow}>
                            <td style={styles.td}>{m}월</td>
                            <td style={{ ...styles.td, color: '#1d4ed8' }}>{d.weekday}회</td>
                            <td style={{ ...styles.td, color: '#059669' }}>{d.weekend}회</td>
                            <td style={{ ...styles.td, fontWeight: '600' }}>{d.weekday + d.weekend}회</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 임원별 집계 */}
              <div style={styles.card}>
                <h3 style={styles.reportTableTitle}>
                  임원별 집계 ({reportYear}년 {reportMonth !== 0 ? `${reportMonth}월` : '전체'})
                </h3>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHead}>
                      <th style={styles.th}>임원</th>
                      <th style={styles.th}>주중</th>
                      <th style={styles.th}>주말</th>
                      <th style={styles.th}>합계</th>
                    </tr>
                  </thead>
                  <tbody>
                    {execList.map(e => {
                      const d = report.byExecutive[e.id] || { weekday: 0, weekend: 0, total: 0 };
                      return (
                        <tr key={e.id} style={styles.tableRow}>
                          <td style={{ ...styles.td, fontWeight: '500' }}>{e.name}</td>
                          <td style={{ ...styles.td, color: '#1d4ed8' }}>{d.weekday}회</td>
                          <td style={{ ...styles.td, color: '#059669' }}>{d.weekend}회</td>
                          <td style={{ ...styles.td, fontWeight: '600' }}>{d.total}회</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* 특정 임원 선택 시: 월별 상세 */}
              {reportExecutive !== 'all' && reportMonth === 0 && (() => {
                const exec = report.byExecutive[reportExecutive];
                if (!exec) return null;
                return (
                  <div style={styles.card}>
                    <h3 style={styles.reportTableTitle}>{exec.name} 월별 상세 ({reportYear}년)</h3>
                    <table style={styles.table}>
                      <thead>
                        <tr style={styles.tableHead}>
                          <th style={styles.th}>월</th>
                          <th style={styles.th}>주중</th>
                          <th style={styles.th}>주말</th>
                          <th style={styles.th}>합계</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => {
                          const md = exec.monthly[m] || { weekday: 0, weekend: 0 };
                          return (
                            <tr key={m} style={styles.tableRow}>
                              <td style={styles.td}>{m}월</td>
                              <td style={{ ...styles.td, color: '#1d4ed8' }}>{md.weekday}회</td>
                              <td style={{ ...styles.td, color: '#059669' }}>{md.weekend}회</td>
                              <td style={{ ...styles.td, fontWeight: '600' }}>{md.weekday + md.weekend}회</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          );
        })()}

      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f4f0' },
  header: {
    backgroundColor: '#92400e', padding: '20px 24px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  headerSub: { color: '#fcd34d', fontSize: '12px' },
  headerName: { color: 'white', fontSize: '18px', fontWeight: '600' },
  logoutBtn: {
    backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.4)',
    color: 'white', padding: '6px 14px', borderRadius: '6px', fontSize: '13px',
  },
  content: { padding: '20px 16px', maxWidth: '700px', margin: '0 auto' },
  tabRow: { display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' },
  tab: {
    flex: 1, padding: '10px 6px', backgroundColor: 'white',
    border: '1px solid #ddd', borderRadius: '8px',
    fontSize: '12px', fontWeight: '500', color: '#666', minWidth: '60px',
  },
  tabActive: { backgroundColor: '#92400e', color: 'white', border: '1px solid #92400e' },
  card: {
    backgroundColor: 'white', borderRadius: '12px',
    padding: '16px', marginBottom: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  cardTitle: { fontSize: '16px', fontWeight: '600', marginBottom: '14px' },
  sectionTitle: { fontSize: '15px', fontWeight: '600', marginBottom: '12px', marginTop: '8px' },
  descText: { fontSize: '13px', color: '#666', marginBottom: '14px', lineHeight: '1.6' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' },
  label: { fontSize: '12px', fontWeight: '500', color: '#555', display: 'block', marginBottom: '4px' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '1px solid #ddd', borderRadius: '8px',
    fontSize: '14px', outline: 'none', backgroundColor: 'white',
  },
  addBtn: {
    width: '100%', padding: '12px', backgroundColor: '#92400e',
    color: 'white', border: 'none', borderRadius: '8px',
    fontSize: '14px', fontWeight: '600',
  },
  userHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '10px',
  },
  userInfo: { display: 'flex', alignItems: 'center', gap: '12px' },
  avatar: {
    width: '40px', height: '40px', borderRadius: '50%',
    backgroundColor: '#fef3c7', color: '#92400e',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '16px', fontWeight: '600',
  },
  userName: { fontSize: '15px', fontWeight: '600', marginBottom: '2px' },
  userEmail: { fontSize: '12px', color: '#666' },
  userDetail: { fontSize: '13px', color: '#666', marginBottom: '4px' },
  roleBadge: { fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px' },
  actionRow: { display: 'flex', gap: '8px', marginTop: '12px' },
  editBtn: {
    flex: 1, padding: '8px', backgroundColor: 'white',
    border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', color: '#444',
  },
  deleteBtn: {
    flex: 1, padding: '8px', backgroundColor: 'white',
    border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '13px', color: '#dc2626',
  },
  membershipRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  emptyBox: {
    backgroundColor: 'white', borderRadius: '10px',
    padding: '32px', textAlign: 'center', color: '#aaa', fontSize: '14px',
  },
  assignedBadge: {
    display: 'flex', alignItems: 'center', gap: '6px',
    backgroundColor: '#ede9fe', color: '#7c3aed',
    padding: '4px 10px', borderRadius: '20px', fontSize: '13px',
  },
  removeBadgeBtn: {
    background: 'none', border: 'none', color: '#7c3aed',
    fontSize: '16px', cursor: 'pointer', padding: '0',
  },
  rulePill: {
    backgroundColor: '#d1fae5', color: '#065f46',
    padding: '3px 10px', borderRadius: '20px', fontSize: '12px',
  },
  rulePillAmber: {
    backgroundColor: '#fef3c7', color: '#92400e',
    padding: '3px 10px', borderRadius: '20px', fontSize: '12px',
  },
  reportFilterRow: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  reportFilterItem: { flex: 1, minWidth: '100px' },
  reportSummaryRow: { display: 'flex', gap: '10px', marginBottom: '12px' },
  reportSummaryCard: {
    flex: 1, backgroundColor: 'white', borderRadius: '12px',
    padding: '16px', textAlign: 'center',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  reportSummaryLabel: { fontSize: '12px', color: '#888', marginBottom: '6px' },
  reportSummaryValue: { fontSize: '24px', fontWeight: '700', color: '#1a1a1a' },
  reportTableTitle: { fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#444' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHead: { backgroundColor: '#f9fafb' },
  th: { padding: '10px 12px', fontSize: '12px', color: '#666', fontWeight: '600', textAlign: 'center', borderBottom: '1px solid #e5e7eb' },
  tableRow: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 12px', fontSize: '14px', color: '#444', textAlign: 'center' },
};